import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useIntentStore, UserIntent, INTENT_METADATA } from '../../stores/intentStore';

/**
 * Intent Mode Switcher
 * 
 * A dropdown component that shows the current intent mode and allows
 * users to switch between modes. Used in the header/toolbar.
 */

interface IntentModeSwitcherProps {
  className?: string;
  variant?: 'default' | 'compact';
}

// Group intents for better UX
const PERSONAL_INTENTS: UserIntent[] = ['identity', 'identity_wallet', 'signature_only', 'wallet_only'];
const COMPANY_INTENTS: UserIntent[] = ['team_identity', 'team_full', 'engagement'];

export const IntentModeSwitcher: React.FC<IntentModeSwitcherProps> = ({
  className = '',
  variant = 'default'
}) => {
  const navigate = useNavigate();
  const { intent, userType, setIntent, setUserType } = useIntentStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const currentMetadata = intent ? INTENT_METADATA[intent] : null;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSelectIntent = (newIntent: UserIntent) => {
    setIntent(newIntent);
    
    // Update userType based on intent
    if (PERSONAL_INTENTS.includes(newIntent)) {
      setUserType('personal');
    } else if (COMPANY_INTENTS.includes(newIntent)) {
      setUserType('company');
    }
    
    setIsOpen(false);
    
    // Navigate based on new intent - always select a default tab
    switch (newIntent) {
      case 'wallet_only':
        // Auto-select wallet tab for wallet-only users
        navigate('/generator?tab=wallet');
        break;
      case 'signature_only':
        // Auto-select email signature tab when switching to signature-only mode
        navigate('/generator?tab=template');
        break;
      case 'identity':
      case 'identity_wallet':
        // Auto-select email signature tab by default for identity modes
        navigate('/generator?tab=template');
        break;
      case 'team_identity':
      case 'team_full':
        navigate('/dashboard');
        break;
      case 'engagement':
        // Logged-in users go to campaigns page, otherwise engagement landing
        navigate('/campaigns');
        break;
    }
  };
  
  if (!intent) {
    return null;
  }
  
  // Show relevant intents based on current user type
  const personalIntents: UserIntent[] = ['identity', 'identity_wallet', 'signature_only', 'wallet_only'];
  const companyIntents: UserIntent[] = ['team_identity', 'team_full', 'engagement'];
  
  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 
          bg-white hover:bg-gray-50 transition-colors
          ${variant === 'compact' ? 'text-sm' : 'text-base'}
        `}
      >
        <span className="text-lg">{currentMetadata?.icon}</span>
        <span className="font-medium text-gray-700">
          {variant === 'compact' ? currentMetadata?.shortTitle : currentMetadata?.title}
        </span>
        <svg 
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 w-80 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-100/50 overflow-hidden z-50"
          >
            <div className="p-1.5">
              {/* Personal Section */}
              <p className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#e11d48]">
                Personal
              </p>
              
              {personalIntents.map((intentOption) => {
                const metadata = INTENT_METADATA[intentOption];
                const isSelected = intent === intentOption;
                
                return (
                  <button
                    key={intentOption}
                    onClick={() => handleSelectIntent(intentOption)}
                    className={`
                      w-full flex items-start gap-3.5 px-4 py-3.5 rounded-xl text-left transition-all
                      ${isSelected 
                        ? 'bg-[#e11d48]/10 border-l-4 border-[#e11d48] text-[#e11d48] hover:bg-[#e11d48]/15' 
                        : 'hover:bg-[#e11d48]/5 hover:text-[#e11d48] text-gray-700'
                      }
                    `}
                  >
                    <span className="text-2xl mt-0.5">{metadata.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{metadata.title}</span>
                        {isSelected && (
                          <svg className="w-4 h-4 text-[#e11d48]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <p className={`text-xs truncate ${isSelected ? 'text-gray-600' : 'text-gray-500'}`}>{metadata.description}</p>
                    </div>
                  </button>
                );
              })}
              
              {/* Divider */}
              <div className="my-2 border-t border-gray-100"></div>
              
              {/* Company Section */}
              <p className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#e11d48]">
                Company
              </p>
              
              {companyIntents.map((intentOption) => {
                const metadata = INTENT_METADATA[intentOption];
                const isSelected = intent === intentOption;
                
                return (
                  <button
                    key={intentOption}
                    onClick={() => handleSelectIntent(intentOption)}
                    className={`
                      w-full flex items-start gap-3.5 px-4 py-3.5 rounded-xl text-left transition-all
                      ${isSelected 
                        ? 'bg-[#e11d48]/10 border-l-4 border-[#e11d48] text-[#e11d48] hover:bg-[#e11d48]/15' 
                        : 'hover:bg-[#e11d48]/5 hover:text-[#e11d48] text-gray-700'
                      }
                    `}
                  >
                    <span className="text-2xl mt-0.5">{metadata.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{metadata.title}</span>
                        {isSelected && (
                          <svg className="w-4 h-4 text-[#e11d48]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <p className={`text-xs truncate ${isSelected ? 'text-gray-600' : 'text-gray-500'}`}>{metadata.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Compact badge version for showing current mode
 */
export const IntentBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { intent } = useIntentStore();
  const metadata = intent ? INTENT_METADATA[intent] : null;
  
  if (!metadata) return null;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full ${className}`}>
      <span className="text-sm">{metadata.icon}</span>
      <span className="text-xs font-medium text-gray-600">{metadata.shortTitle}</span>
    </div>
  );
};

export default IntentModeSwitcher;
